import React from 'react';
import { useTranslation } from 'react-i18next';

const Turnover: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section id="turnover" className="py-8 sm:py-12 md:py-16 px-3 sm:px-4 md:px-6 lg:px-8 bg-dark-900 border-b border-white/5 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-0 w-48 sm:w-64 md:w-96 h-48 sm:h-64 md:h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-48 sm:w-64 md:w-96 h-48 sm:h-64 md:h-96 bg-green-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-6 sm:mb-8 md:mb-12">
          <div className="inline-block mb-2 sm:mb-4">
            <span className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">
              {t('landing.turnover.exclusiveFeature')}
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-4">
            <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
              {t('landing.turnover.title')}
            </span>
            <br />
            <span className="text-white">{t('landing.turnover.multiplierBonus')}</span>
          </h2>
          <p className="text-gray-300 text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl mx-auto px-2">
            {t('landing.turnover.description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
          {/* Left Side - Image and Visual */}
          <div className="relative">
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
              {/* AI Generated Image Placeholder - Replace with actual AI image */}
              <div className="aspect-[4/3] sm:aspect-square bg-gradient-to-br from-green-500/20 via-blue-500/20 to-purple-500/20 relative">
                <img
                  src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&h=800&fit=crop&q=80"
                  alt="20X Wagering Requirement"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover opacity-90"
                  onError={(e) => {
                    // Fallback to gradient if image fails
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/30 via-blue-500/30 to-purple-500/30 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-bold text-white drop-shadow-2xl mb-2 sm:mb-4">
                      20X
                    </div>
                    <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white font-semibold drop-shadow-lg">
                      WAGERING
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 bg-gradient-to-r from-green-500 to-blue-500 text-white px-3 sm:px-6 py-1.5 sm:py-3 rounded-full shadow-xl transform rotate-12 animate-pulse">
              <div className="text-lg sm:text-2xl font-bold">20X</div>
              <div className="text-[10px] sm:text-xs">WAGER</div>
            </div>
          </div>

          {/* Right Side - Features and Benefits */}
          <div className="space-y-3 sm:space-y-4 md:space-y-6">
            <div className="bg-dark-800/80 backdrop-blur-sm border border-dark-700 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 hover:border-green-500 transition-all duration-300">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm sm:text-base md:text-xl font-bold text-white mb-1 sm:mb-2">{t('landing.turnover.multiplyWinnings')}</h3>
                  <p className="text-gray-300 text-xs sm:text-sm md:text-base">
                    {t('landing.turnover.multiplyWinningsDesc')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-dark-800/80 backdrop-blur-sm border border-dark-700 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 hover:border-blue-500 transition-all duration-300">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm sm:text-base md:text-xl font-bold text-white mb-1 sm:mb-2">{t('landing.turnover.easyToQualify')}</h3>
                  <p className="text-gray-300 text-xs sm:text-sm md:text-base">
                    {t('landing.turnover.easyToQualifyDesc')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-dark-800/80 backdrop-blur-sm border border-dark-700 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 hover:border-purple-500 transition-all duration-300">
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm sm:text-base md:text-xl font-bold text-white mb-1 sm:mb-2">{t('landing.turnover.secureFair')}</h3>
                  <p className="text-gray-300 text-xs sm:text-sm md:text-base">
                    {t('landing.turnover.secureFairDesc')}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="pt-2 sm:pt-4">
              <button className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-2.5 sm:py-3 md:py-4 px-4 sm:px-6 md:px-8 rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg text-sm sm:text-base md:text-lg">
                {t('landing.turnover.claimBonus')}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-8 sm:mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 md:gap-6">
          <div className="text-center bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-green-400 mb-1 sm:mb-2">20X</div>
            <div className="text-gray-200 text-xs sm:text-sm">{t('landing.turnover.multiplier')}</div>
          </div>
          <div className="text-center bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-400 mb-1 sm:mb-2">24/7</div>
            <div className="text-gray-200 text-xs sm:text-sm">{t('landing.turnover.available')}</div>
          </div>
          <div className="text-center bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-400 mb-1 sm:mb-2">100%</div>
            <div className="text-gray-200 text-xs sm:text-sm">{t('landing.turnover.secure')}</div>
          </div>
          <div className="text-center bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6">
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 mb-1 sm:mb-2">∞</div>
            <div className="text-gray-200 text-xs sm:text-sm">{t('landing.turnover.unlimited')}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Turnover;

